
import type { PropType } from 'vue';

import _debounce from 'lodash/debounce';

import axios from 'axios';

import {
  defineComponent,
  ref,
  computed,
  watch,
  onMounted,
} from 'vue';

import PrimeVueListBox from 'primevue/listbox';

import PrimeVueInputText from 'primevue/inputtext';

import { AUTOCOMPLETE_ENTRIES_LIMIT } from '@/libs/consts';

import { Option } from '@/types';

// То же, что FieldListBox, но справочник живёт на сервере: список городов целиком
// в память браузера не тянется. Поиск и каскадный фильтр уходят в API.
//
// Встроенный filter у ListBox не используется намеренно: visibleOptions прогоняет
// FilterService поверх выдачи, и он вычищал бы список ещё до ответа сервера, заодно
// пряча выбранное, не совпавшее с набранным текстом. Свой инпут в #header, как в
// FieldDropdownAutoCompleteMultiple.
export default defineComponent({
  components: {
    PrimeVueListBox,
    PrimeVueInputText,
  },
  props: {
    modelValue: {
      type: Array as PropType<number[]>,
      required: true,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    apiUrl: {
      type: String,
      required: true,
    },
    apiParams: {
      type: Object,
      default: () => ({}),
    },
    optionLabel: {
      type: String,
      default: 'compiled_name',
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const options = ref<any[]>([]);

    const keyword = ref();

    const loading = ref(false);

    const fetched = ref(false);

    const sortedOptions = computed((): Option[] => options.value
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((option: any) => ({
        value: option.id,
        label: option[props.optionLabel],
      }))
      .sort((a, b) => {
        const aSelected = Number(props.modelValue.includes(a.value));

        const bSelected = Number(props.modelValue.includes(b.value));

        return bSelected - aSelected || a.label.localeCompare(b.label);
      }));

    const getOptions = async (): Promise<void> => {
      loading.value = true;

      try {
        const response = await axios.get(
          props.apiUrl,
          {
            params: {
              // Подписи уже выбранного: на первой загрузке его в выдаче может не быть
              'filter[id]': props.modelValue.length && !options.value.length ? props.modelValue.join(',') : undefined,
              'filter[name]': keyword.value || undefined,
              per_page: AUTOCOMPLETE_ENTRIES_LIMIT,
              ...props.apiParams,
            },
          },
        );

        // Выбранное сохраняем поверх новой выдачи, иначе оно пропадёт из списка
        const mergedOptions = [
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...options.value.filter((option: any) => props.modelValue.includes(option.id)),
          ...response.data.data,
        ];

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        options.value = [...new Map(mergedOptions.map((option: any) => [option.id, option])).values()];
      } catch (err) {
        console.error(err);
      }

      loading.value = false;

      fetched.value = true;
    };

    watch(keyword, _debounce(() => getOptions(), 500));

    // Сменился каскадный фильтр — часть выбранного могла стать невалидной. Отсеиваем
    // отдельным запросом, а не по выдаче: она постраничная (per_page = лимит автокомплита),
    // и валидное значение с дальней страницы фильтрация по items выбросила бы зря
    const pruneModelValue = async (): Promise<void> => {
      if (!props.modelValue.length) {
        return;
      }

      try {
        const response = await axios.get(
          props.apiUrl,
          {
            params: {
              'filter[id]': props.modelValue.join(','),
              per_page: props.modelValue.length,
              ...props.apiParams,
            },
          },
        );

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const validIds = response.data.data.map((option: any) => option.id);

        if (validIds.length !== props.modelValue.length) {
          emit('update:modelValue', props.modelValue.filter((id) => validIds.includes(id)));
        }
      } catch (err) {
        console.error(err);
      }
    };

    watch(() => props.apiParams, () => {
      getOptions();

      pruneModelValue();
    });

    onMounted(() => getOptions());

    return {
      sortedOptions,
      keyword,
      loading,
      fetched,
    };
  },
});
